import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '@/api';
import { toast } from 'sonner';

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'admin' | 'branch';
  branch_id?: string | null;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  loading: false,
  login: async () => {},
  logout: async () => {},
  isAuthenticated: false,
});

export const useAuth = () => useContext(AuthContext);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const [user, setUser] = useState<User | null>(() => {
    try {
      const storedUser = localStorage.getItem('user');
      if (!storedUser || storedUser === 'undefined') return null;
      return JSON.parse(storedUser);
    } catch {
      return null;
    }
  });

  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Restore token and set Authorization header
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, []);

  // Fetch /me to verify user
  useEffect(() => {
    api
      .get('/me')
      .then((res) => {
        if (res.data.user) {
          setUser(res.data.user);
          localStorage.setItem('user', JSON.stringify(res.data.user));
        } else {
          throw new Error('No user returned');
        }
      })
      .catch(() => {
        setUser(null);
        localStorage.clear();
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      const res = await api.post('/login', { email, password });

      localStorage.setItem('token', res.data.token);
      api.defaults.headers.common['Authorization'] = `Bearer ${res.data.token}`;

      setUser(res.data.user);
      localStorage.setItem('user', JSON.stringify(res.data.user));

      const from = (location.state as any)?.from?.pathname;
      // Redirect branch users to POS, admin users to Dashboard
      const defaultRedirect = res.data.user.role === 'branch' ? '/sales' : '/';
      const redirectTo = from && from !== '/login' ? from : defaultRedirect;
      navigate(redirectTo, { replace: true });
    } catch (error: any) {
      const msg = error.response?.data?.error || 'Invalid credentials';
      toast.error(msg);
      return Promise.reject(new Error(msg));
    } finally {
      setLoading(false);
    }
  };

  const logout = async () => {
    setLoading(true);
    try {
      await api.post('/logout');
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setUser(null);
      localStorage.clear();
      sessionStorage.clear();
      navigate('/login', { replace: true });
      setLoading(false);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        login,
        logout,
        isAuthenticated: !!user,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
